import React, { useState, useEffect, useRef } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CCloseButton, CFormLabel, CFormInput,
} from '@coreui/react';
import { toast } from 'react-toastify';
import AxiosInstance from 'src/components/Axios';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { getAttachmentKind, formatFileSize, KIND_ICONS } from 'src/utils/attachmentType';
import {
	invalidateCachedRequest, invalidateLettersList,
	invalidateCachedSession, invalidateCachedSupportHistory,
} from 'src/utils/supportRequestCache';

const urlApiRequests = '/support-requests';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 МБ

/**
 * Модалка завантаження вкладення до листа підтримки.
 * За один запит бекенд створює `MediaObject` і зв'язок з листом,
 * тому запит виконується одразу — без відкладеного сабміту форми.
 */
const SupportLetterAttachmentUploadModal = ({ open, onClose, requestId, sessionId, userEmail, onSuccess }) => {
	const [file, setFile] = useState(null);
	const [name, setName] = useState('');
	const [previewUrl, setPreviewUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const fileInputRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		setFile(null);
		setName('');
		setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
		if (fileInputRef.current) fileInputRef.current.value = '';
	}, [open]);

	/*
	 * Guard `if (loading)` тут захищає тільки від того, щоб ми самі не скинули
	 * `previewUrl` і не смикнули `onClose` посеред запиту. Зупинити закриття
	 * модалки він НЕ може — CoreUI ховає її власним внутрішнім стейтом ще до
	 * того, як викличе цей колбек (деталі — у коментарі до `<CModal>` нижче).
	 */
	const handleClose = () => {
		if (loading) return;
		setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
		onClose();
	};

	const handleFileChange = (e) => {
		const selected = e.target.files?.[0] || null;
		const kind = selected ? getAttachmentKind(selected.name) : null;
		setPreviewUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return selected && kind === 'image' ? URL.createObjectURL(selected) : null;
		});
		setFile(selected);
		if (selected && !name.trim()) setName(selected.name);
	};

	const handleSubmit = async () => {
		if (!file) return;
		if (file.size > MAX_FILE_SIZE) {
			toast.warning('Розмір файлу не повинен перевищувати 10 МБ');
			return;
		}
		setLoading(true);
		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('name', name.trim() || file.name);
			const res = await AxiosInstance.post(`${urlApiRequests}/${requestId}/attachments`, formData);
			invalidateCachedRequest(requestId);
			invalidateLettersList();
			if (sessionId) invalidateCachedSession(sessionId);
			if (userEmail) invalidateCachedSupportHistory(userEmail);
			toast.success('Вкладення додано');
			setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
			onClose();
			onSuccess(res.data);
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка завантаження вкладення');
		} finally {
			setLoading(false);
		}
	};

	const fileKind = file ? getAttachmentKind(file.name) : null;

	return (
		<CModal
			visible={open}
			onClose={handleClose}
			/*
			 * `CModal` у CoreUI 4 — НЕ повністю контрольований компонент, тому
			 * повністю заборонити закриття під час завантаження не виходить.
			 * Що саме заважає (за джерелом `@coreui/react/dist/index.js`):
			 *
			 * 1. `handleDismiss` (backdrop-клік і Escape) робить `setVisible(false)`
			 *    ще ДО виклику `onClose` — модалка ховається внутрішнім стейтом
			 *    незалежно від того, що поверне батьківський колбек.
			 * 2. Пропси `backdrop`/`keyboard` нижче для вже ВІДКРИТОЇ модалки не
			 *    діють: слухачі `mouseup`/`keydown` вішаються в `useEffect` із
			 *    залежністю `[_visible]`, тобто один раз на відкриття, і замикають
			 *    у собі значення пропсів з того рендеру. Модалку відкривають при
			 *    `loading === false`, тож у closure назавжди лишається
			 *    `backdrop: true, keyboard: true`, і подальша зміна на них не
			 *    впливає. Спрацьовують вони лише якщо модалку відкрити ЗАНОВО,
			 *    поки запит ще йде — тоді слухачі перереєструються вже зі `static`.
			 * 3. Штатний хрестик `CModalHeader` робить `setVisible(false)` напряму
			 *    і `onClose` взагалі не викликає — тому тут `closeButton={false}`,
			 *    а хрестик рендеримо свій, із `disabled`. Це єдиний із трьох
			 *    способів закриття, який вдається заблокувати надійно.
			 *
			 * ПОТОЧНА ПОВЕДІНКА (усвідомлена, узгоджена з Андрієм 28.09.2026):
			 * Escape і клік поза вікном МОЖУТЬ закрити модалку посеред завантаження.
			 * Це нешкідливо: запит не переривається, вкладення долітає і додається
			 * в блок, `onSuccess` оновлює `data`. Якщо адмін встигне відкрити
			 * модалку повторно — побачить ту саму, все ще заблоковану оверлеєм, і
			 * додати друге вкладення паралельно не зможе. Дублікат не створиться.
			 *
			 * Якщо колись знадобиться жорстка заборона — або підняти CoreUI до
			 * версії, де слухачі перереєструються, або тримати власний backdrop.
			 */
			backdrop={loading ? 'static' : true}
			keyboard={!loading}
		>
			<CModalHeader closeButton={false}>
				<CModalTitle>Додати вкладення до листа #{requestId}</CModalTitle>
				<CCloseButton onClick={handleClose} disabled={loading} />
			</CModalHeader>
			<CModalBody>
				<FormSubmitOverlay submitting={loading}>
					<CFormLabel>Файл *</CFormLabel>
					<input
						ref={fileInputRef}
						type="file"
						className="form-control"
						onChange={handleFileChange}
					/>
					{file && (
						<div className="mt-2">
							{previewUrl ? (
								<img src={previewUrl} alt="Прев'ю" style={{ height: '120px', display: 'block' }} />
							) : (
								<div style={{ fontSize: '2rem', lineHeight: 1 }}>{KIND_ICONS[fileKind]}</div>
							)}
							<div className="text-muted small mt-1">
								Обрано: {file.name} ({formatFileSize(file.size)})
							</div>
						</div>
					)}
					<div className="mt-3">
						<CFormLabel>Назва</CFormLabel>
						<CFormInput
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Назва"
							disabled={loading}
							autoComplete="off"
						/>
					</div>
				</FormSubmitOverlay>
			</CModalBody>
			<CModalFooter>
				<CButton color="primary" onClick={handleSubmit} disabled={!file || loading} title="Завантажити вкладення">
					{loading ? 'Завантаження...' : 'Зберегти'}
				</CButton>
				<CButton color="secondary" variant="outline" onClick={handleClose} disabled={loading} title="Скасувати">
					Скасувати
				</CButton>
			</CModalFooter>
		</CModal>
	);
};

export default SupportLetterAttachmentUploadModal;
